// Sign-up chatbot entry. Two mount modes share one bundle:
//   1. Dedicated page — auto-mounts SignupFlow onto the Slim `#signup-root` scaffold
//      (app/views/signup_flows/_chatbot.html.slim), mirroring cmg_simulator.ts.
//   2. Drawer — `window.openSignupOverlay()` mounts SignupFlow on demand into a
//      bottom-right drawer that slides up from the bottom (chat-widget style, NO dimmed
//      backdrop — the page stays visible and usable behind it). Closes via ✕ or Esc.
//      Triggered from a button anywhere (e.g. the visitor navbar); props come from a
//      hidden `#signup-overlay-config` element rendered on the host page.
//
// Note: this entry does NOT import '@twg/ui/styles'. The sign-up chat UI is bespoke; Rails
// serves application.css separately. Mount-timing model (like cmg_simulator.ts): module
// scripts are deferred, so we mount on first exec AND on turbo:load; `__vueApp__` guards it.
import { createApp, type App } from 'vue'
import {
  SignupFlow,
  installSignupCtaInterceptor,
  watchSignupExperimentTrigger,
  buildSignupDrawer,
} from '@twg/sign-up'

interface SignupElement extends HTMLElement {
  __vueApp__?: App
}

interface SignupProps {
  brand: string
  locale: string
  locationiqKey: string
  countryCodes: string
}

function readProps(el: HTMLElement | null): SignupProps {
  return {
    brand: el?.dataset.brand || 'nounou-top',
    locale: el?.dataset.locale || 'fr',
    // Address autocomplete (LocationIQ): the public map key + brand country codes are
    // client-exposed by design (same as the legacy addresses/_input.html.slim page).
    locationiqKey: el?.dataset.locationiqKey || '',
    countryCodes: el?.dataset.countryCodes || 'FR',
  }
}

// Single SignupFlow app factory — shared by both mount modes so there's exactly one
// createApp(SignupFlow) call site (keeps vue/one-component-per-file happy).
function createSignupApp(props: SignupProps): App {
  return createApp(SignupFlow, { ...props })
}

// ── Mode 1: dedicated-page auto-mount on #signup-root ──────────────────────────
function mountSignup(): void {
  const el = document.getElementById('signup-root') as SignupElement | null
  if (!el) return
  if (el.__vueApp__) return // already mounted — defensive against double turbo:load

  const variant = el.dataset.variant
  // Seed the A/B funnel variant BEFORE mount so useFunnelVariant() picks up a server value.
  if (variant && variant.length > 0) {
    ;(window as unknown as { AB_VARIANT?: string }).AB_VARIANT = variant
  }

  const app = createSignupApp(readProps(el))
  app.mount(el)
  el.__vueApp__ = app
}

function unmountSignup(): void {
  const el = document.getElementById('signup-root') as SignupElement | null
  if (!el?.__vueApp__) return
  el.__vueApp__.unmount()
  delete el.__vueApp__
}

// ── Mode 2: on-demand bottom-right drawer ──────────────────────────────────────
const DRAWER_ANIM_MS = 380 // keep in sync with the .signup-drawer transition below

let overlayApp: App | null = null
let overlayEl: HTMLElement | null = null
let overlayClosing = false
let overlayOpener: HTMLElement | null = null // element focused before the drawer opened

// The drawer chrome, injected once into <head>. Media queries (mobile = full-screen
// sheet, ≥640px = floating 440px panel anchored bottom-right) + the slide-up transform
// can't be expressed with inline styles, hence a stylesheet. No dimmed backdrop: the
// wrapper only covers the panel area on desktop, so the page stays clickable behind it.
function ensureDrawerStyles(): void {
  if (document.getElementById('signup-drawer-styles')) return
  const style = document.createElement('style')
  style.id = 'signup-drawer-styles'
  style.textContent = `
.signup-drawer {
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 9999;
  width: 100%;
  height: 100dvh;
  transform: translateY(100%);
  transition: transform ${DRAWER_ANIM_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1);
  will-change: transform;
}
.signup-drawer.is-open { transform: translateY(0); }
@media (min-width: 640px) {
  .signup-drawer {
    right: 24px;
    bottom: 24px;
    width: 440px;
    height: auto;
    max-height: calc(100dvh - 48px);
  }
}
.signup-drawer__close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.08);
  color: #1a2733;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.signup-drawer__close:hover { background: rgba(15, 23, 42, 0.16); }
@media (prefers-reduced-motion: reduce) {
  .signup-drawer { transition: none; }
}
`
  document.head.appendChild(style)
}

function onOverlayKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') closeSignupOverlay()
}

// Immediate teardown (no animation) — used once the slide-down settles and on
// turbo:before-cache so a closing drawer is never captured in the page snapshot.
function destroyOverlay(): void {
  if (!overlayEl) return
  overlayApp?.unmount()
  overlayApp = null
  document.removeEventListener('keydown', onOverlayKeydown)
  overlayEl.remove()
  overlayEl = null
  overlayClosing = false
  overlayOpener?.focus() // hand keyboard focus back to the CTA that opened the dialog
  overlayOpener = null
}

function closeSignupOverlay(): void {
  if (!overlayEl || overlayClosing) return
  overlayClosing = true
  overlayEl.classList.remove('is-open') // slide down, then tear down once it settles
  window.setTimeout(destroyOverlay, DRAWER_ANIM_MS)
}

function openSignupOverlay(): void {
  if (overlayEl) return // already open (or mid-close) — ignore

  ensureDrawerStyles()
  overlayOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null

  const { drawer, closeButton, mount, focusIn } = buildSignupDrawer()
  closeButton.addEventListener('click', closeSignupOverlay)
  document.body.appendChild(drawer)
  overlayEl = drawer

  const cfg = document.getElementById('signup-overlay-config')
  overlayApp = createSignupApp(readProps(cfg))
  overlayApp.mount(mount)

  // Force a reflow so the initial translateY(100%) is committed before we flip to
  // is-open — otherwise the browser batches both writes and the slide-up is skipped.
  void drawer.offsetHeight
  drawer.classList.add('is-open')
  focusIn()

  document.addEventListener('keydown', onOverlayKeydown)
}

// ── A/B treatment hook: rewire the legacy signup CTAs to open the overlay ───────
// The Amplitude Web Experiment treatment injects a #signup-chatbot-trigger marker (or calls
// window.initSignupChatButton() directly); control does nothing (the CTAs keep opening the
// legacy modal). Interception is delegated at the document level (see
// installSignupCtaInterceptor), so CTAs that enter the DOM later — Turbo swaps, the mobile
// menu's popover-rendered link — are covered without re-binding. Idempotent.
let signupCtaHookInstalled = false
function initSignupChatButton(): void {
  if (signupCtaHookInstalled) return
  signupCtaHookInstalled = true
  installSignupCtaInterceptor(openSignupOverlay)
}

// The marker may land at any time after load (the experiment SDK fetches its remote config
// first), so the page is observed rather than polled; re-armed on each Turbo navigation.
let stopExperimentWatch: (() => void) | null = null
function armExperimentWatcher(): void {
  stopExperimentWatch?.()
  stopExperimentWatch = watchSignupExperimentTrigger(initSignupChatButton)
}

// Expose the hooks so cross-bundle callers reach them over the Shakapacker↔Vite boundary:
// openSignupOverlay for the Stimulus button, initSignupChatButton for the A/B treatment script.
const signupGlobals = window as unknown as {
  openSignupOverlay?: () => void
  initSignupChatButton?: () => void
}
signupGlobals.openSignupOverlay = openSignupOverlay
signupGlobals.initSignupChatButton = initSignupChatButton

// Dedicated-page lifecycle (harmless no-op on pages without #signup-root, e.g. overlay hosts).
mountSignup()
armExperimentWatcher()
document.addEventListener('turbo:load', () => {
  mountSignup()
  armExperimentWatcher()
})
document.addEventListener('turbo:before-cache', () => {
  unmountSignup()
  destroyOverlay() // immediate (skip the slide-down) so the snapshot is clean
})
